<template>
  <v-app :class="computedStyles">
    <template v-if="isRoutePartnerRelated">
      <slot />
    </template>
    <template v-else>
      <status-banner class="disable-print" />
      <AppHeader class="disable-print" />
      <AppNavigation class="disable-print" />
      <v-main
        class="grey lighten-5"
        :style="vMainStyles"
      >
        <slot />
      </v-main>
      <InviteDialog />
      <FeedbackDialog />
      <create-account />
      <FilePreview />
      <snackbar />
      <OrganizationInviteConfirmDialog v-if="isAuthenticated" />
      <SetSecurityQuestionsPrompt :value="!isMimic" />
      <OTPasswordPrompt :value="!isMimic" />
      <TermsOfUseDialog v-if="shouldShowTermsOfUsePrompt" />
      <EULADialog v-if="shouldShowEULAPrompt && !shouldShowTermsOfUsePrompt" />
      <AudioPlayer />
      <OutageOverlay />
      <AdblockDialog />
      <CouponDialog v-if="showTimedSalesDialog" />
      <PartnerPopupDialog v-if="isAuthenticated" />
      <WebinarModal v-if="flags['webinars']" />
      <WebinarMinimized v-if="flags['webinars']" />
      <FloatingAssistantBtn
        v-if="showFloatingAssistantBtn"
      />
      <Disclaimer
        v-if="!shouldHideDisclaimer"
        v-model="showDisclaimerDialog"
      />
      <div
        v-if="!shouldHideDisclaimer"
        class="disclaimer-text pb-12 text--darken-4"
        :style="disclaimerStyles"
      >
        <span class="font-weight-bold">Disclaimer:</span> The information and services contained on the Website,
        the {{ $appName }} Software, and Sinoa are for educational and informational purposes only and are not intended
        to be an exhaustive or definitive examination or to provide you with any advice on the subject of farming,
        sales, field monitoring, data management, or farm management practices and should not be relied upon by you
        for such matters. You are solely responsible for your decisions and {{ $appName }} Corp. disclaims any liability
        with respect to same.
        <a
          class="text-underline"
          @click.prevent="showDisclaimerDialog = true"
        >Read the complete disclaimer.</a>
      </div>
    </template>
  </v-app>
</template>

<script>
import Vue from 'vue';
import { mapState, mapMutations, mapGetters, mapActions } from 'vuex';
import StatusBanner from './StatusBanner.vue';
import AppHeader from './AppHeader.vue';
import AppNavigation from './AppNavigation.vue';
import InviteDialog from './InviteDialog.vue';
import FeedbackDialog from './FeedbackDialog.vue';
import FilePreview from '@/components/library/FilePreview.vue';
import Snackbar from './Snackbar.vue';
import CreateAccount from './CreateAccount.vue';
import TermsOfUseDialog from '../legal/TermsOfUseDialog.vue';
import EULADialog from '../legal/EULADialog.vue';
import OrganizationInviteConfirmDialog from './OrganizationInviteConfirmDialog.vue';
import SetSecurityQuestionsPrompt from '@/components/dashboard/SetSecurityQuestionsPrompt.vue';
import OTPasswordPrompt from '@/components/dashboard/OTPasswordPrompt.vue';
import AudioPlayer from '@/components/library/AudioPlayer.vue';
import OutageOverlay from '@/components/overlays/OutageOverlay.vue';
import AdblockDialog from '@/components/overlays/AdblockDialog.vue';
import CouponDialog from '../coupons/CouponDialog.vue';
import FloatingAssistantBtn from '../chatbot/FloatingAssistantBtn.vue';
import Disclaimer from '@/components/dashboard/DashboardDisclaimer.vue';
import WebinarModal from '@/components/webinars/WebinarModal.vue';
import PartnerPopupDialog from '@/components/partnerPopups/PartnerPopupDialog.vue';
import WebinarMinimized from '@/components/webinars/WebinarMinimized.vue';

export default {
  name: 'GrainFox',

  components: {
    AudioPlayer,
    TermsOfUseDialog,
    EULADialog,
    StatusBanner,
    AppHeader,
    AppNavigation,
    InviteDialog,
    FeedbackDialog,
    FilePreview,
    Snackbar,
    CreateAccount,
    OrganizationInviteConfirmDialog,
    SetSecurityQuestionsPrompt,
    OTPasswordPrompt,
    OutageOverlay,
    AdblockDialog,
    CouponDialog,
    FloatingAssistantBtn,
    Disclaimer,
    WebinarModal,
    WebinarMinimized,
    PartnerPopupDialog,
  },

  data() {
    return {
      lastScrollTop: 0,
      scrollDetected: false,
      showDisclaimerDialog: false,
    };
  },

  computed: {
    ...mapState('launchDarkly', ['flags']),
    ...mapState('app', ['isPrintingEnabled', 'isMimic', 'navDrawerWidth']),
    ...mapState('user', ['shouldShowTermsOfUsePrompt', 'shouldShowEULAPrompt']),
    ...mapGetters('user', ['isAuthenticated']),
    ...mapState('chatbot', ['isGrainFoxAssistantDialogOpen']),
    ...mapGetters('permissions', ['checkPermission']),

    computedStyles() {
      return {
        'mimic-mode': this.isMimic,
        'disable-print': !this.isPrintingEnabled,
      };
    },

    disclaimerStyles() {
      return {
        'padding-left': `${this.navigationOffset + 16}px`,
      };
    },

    vMainStyles() {
      return {
        'padding-left': `${this.navigationOffset}px`,
      };
    },

    isMobile() {
      return this.$vuetify.breakpoint.xs || this.$vuetify.breakpoint.sm;
    },

    isTablet() {
      return this.$vuetify.breakpoint.md;
    },

    isDesktop() {
      return this.$vuetify.breakpoint.lg || this.$vuetify.breakpoint.xl;
    },

    headerOffset() {
      return this.$vuetify.application.top + this.$vuetify.application.bar;
    },

    navigationOffset() {
      return this.isAuthenticated && !this.$isMobile ? this.navDrawerWidth : 0;
    },

    showTimedSalesDialog() {
      return this.flags['timed-coupon'] && DJANGO_REQUEST.user?.isLoggedIn;
    },

    isRoutePartnerRelated() {
      // Routes that render as standalone full-screen pages — no nav, header, or footer.
      const overlayRoutes = ['/partner-redirect/', '/webinar-registration/', '/risk-profile/'];
      return overlayRoutes.includes(this.$route.path);
    },

    shouldHideDisclaimer() {
      const { path } = this.$route;
      const excludedPaths = [
        '/login/',
        '/register/',
        '/password_reset/',
        '/privacy_policy/',
        '/terms_of_use/',
      ];
      // Check exact matches and paths that start with excluded patterns (e.g., /reset/...)
      return excludedPaths.some((excludedPath) => path === excludedPath) || path.startsWith('/reset/');
    },

    showFloatingAssistantBtn() {
      return this.flags['grainfox-ai'] && DJANGO_REQUEST.user?.isLoggedIn && !this.scrollDetected
       && this.checkPermission('ai_chatbot', 'read');
    },
  },

  created() {
    Object.defineProperty(Vue.prototype, '$isMobile', {
      get: () => this.isMobile,
    });

    Object.defineProperty(Vue.prototype, '$isTablet', {
      get: () => this.isTablet,
    });

    Object.defineProperty(Vue.prototype, '$isDesktop', {
      get: () => this.isDesktop,
    });

    Object.defineProperty(Vue.prototype, '$headerOffset', {
      get: () => this.headerOffset,
    });

    Object.defineProperty(Vue.prototype, '$navigationOffset', {
      get: () => this.navigationOffset,
    });

    this.initializeVuex();
  },

  async mounted() {
    window.addEventListener('scroll', this.handleScroll);

    window.addEventListener('resize', () => {
      this.setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    });
    this.setWindowSize({
      width: window.innerWidth,
      height: window.innerHeight,
    });
    await this.detectAdBlock();
  },

  methods: {
    ...mapMutations('app', ['setWindowSize', 'setUnreadNotificationCount']),
    ...mapMutations('chat', ['setUnreadMessagesCount']),
    ...mapActions('app', ['loadAppState', 'loadNavDrawerItems', 'detectAdBlock', 'handleMimicChanged']),
    ...mapActions('user', ['loadUserState']),
    ...mapActions('app', ['loadAppBrandState']),
    ...mapActions('group', ['loadGroupState']),
    ...mapActions('chargebee', ['loadChargebeeState', 'handleChargebeeWebhook']),
    ...mapActions('preferences', ['loadPreferencesState']),
    ...mapActions('subscription', ['loadSubscriptionState']),
    ...mapActions('permissions', ['loadPermissionsState']),

    async initializeVuex() {
      this.loadAppState(DJANGO_REQUEST.app);
      this.loadAppBrandState(DJANGO_REQUEST.appBrand);
      this.loadUserState(DJANGO_REQUEST.user);
      this.loadGroupState(DJANGO_REQUEST.group);
      this.loadChargebeeState(DJANGO_REQUEST.chargebee);
      this.loadPreferencesState(DJANGO_REQUEST.preferences);
      this.loadSubscriptionState(DJANGO_REQUEST.subscription);
      this.loadPermissionsState(DJANGO_REQUEST.permissions);
      this.setUnreadMessagesCount(DJANGO_REQUEST.unreadChatMessagesCount);
      this.setUnreadNotificationCount(DJANGO_REQUEST.unreadTotalNotificationsCount);

      if (DJANGO_REQUEST.user?.isLoggedIn) {
        this.loadNavDrawerItems();
        this.$gfws.registerCallback('customer_data_updated', this.handleChargebeeWebhook);
        this.$gfws.registerCallback('mimic_state_updated', this.handleMimicChanged);
      }
    },

    handleScroll() {
      if (this.isGrainFoxAssistantDialogOpen) {
        this.scrollDetected = false;
        return;
      }
      const currentScrollTop = window.pageYOffset || document.documentElement.scrollTop;
      if (currentScrollTop < this.lastScrollTop) {
        this.scrollDetected = false;
      } else if (currentScrollTop > this.lastScrollTop) {
        this.scrollDetected = true;
      }
      this.lastScrollTop = currentScrollTop <= 0 ? 0 : currentScrollTop;
    },

  },
};
</script>

<style lang="scss" scoped>
@media print {
  .v-main {
    padding: 0px !important;
  }
}

.disclaimer-text {
  font-size: 10px;
  line-height: 18px;
}
</style>
